import { useState, useMemo, useCallback } from "react";
import { styled, Button } from "@mui/material";
import plusIcon from "../icons/plusIcon.svg";
import { nFormatter } from "../../utils/parseNumber";
import colors from "../../theme/themeConstants";

interface IProps {
  tags: any[];
  expanded?: boolean;
  showAmount?: boolean;
  setTags: (tags: any) => void;
  mainSelectedTags: string[];
  invertColor?: boolean;
}

const PAGE_SIZE = 30;

const SelectTags = (props: IProps) => {
  const { tags, expanded, showAmount, setTags, mainSelectedTags: selectedTags, invertColor } = props;
  const [page, setPage] = useState(2);
  const mainSelectedTags = useMemo(() => selectedTags || [], [selectedTags]);

  const amountOfShrinkedTags = PAGE_SIZE * page;

  const paginatedTags = useMemo(() => {
    if (!tags) {
      return [];
    }

    const newTags = tags.slice(0, PAGE_SIZE * page);

    return newTags.filter((tag: any) => !mainSelectedTags.includes(tag.name));
  }, [tags, mainSelectedTags, page]);

  const handleTagClick = useCallback((tag: any) => (event: React.MouseEvent) => {
    event.stopPropagation();
    let tagText = tag.name;
    if (showAmount && tag.name) {
      tagText = tag.name;
    }

    if (tagText !== null) {
      setTags([...mainSelectedTags, tagText]);
    } else {
      // eslint-disable-next-line
      console.warn("Oops, an empty tag clicked.");
    }
  }, [showAmount, mainSelectedTags, setTags]);

  const onMoreClick = () => {
    setPage(page + 1);
  };

  const handleTagDelete = useCallback((tag: any) => {
    if (mainSelectedTags.includes(tag)) {
      setTags(
        mainSelectedTags.filter((t: any) => t !== tag)
      );
    }
  }, [mainSelectedTags, setTags]);

  return (
    <Container id="tagsblock" isExpanded={Boolean(expanded)} >
      {mainSelectedTags && mainSelectedTags?.map((el: any, index: number) => (
        <Button
          onClick={() => handleTagDelete(el)}
          sx={{
            background: colors.redPink,
            color: colors.white,
            textShadow: invertColor ? "none" : "black 1px 0 5px",
            whiteSpace: "nowrap",
          }}
          key={index}
        >{el}</Button>
      ))}
      {(tags && tags.length > 0) ? paginatedTags?.map((tag: any, index: number) =>
        <Button
          onClick={handleTagClick(tag)}
          sx={{
            background: "transparent",
            color: colors.white,
            display: `${index < amountOfShrinkedTags ? "inline-block" : "none"}`,
            textShadow: invertColor ? "none" : "black 1px 0 5px",
            whiteSpace: "nowrap",
          }}
          key={index}
        >
          {showAmount ? <span>{tag.name}{showAmount ? <p
            style={{
              opacity: 0.6,
              display: "inline",
              marginLeft: "3px",
              // eslint-disable-next-line no-magic-numbers
            }}>{nFormatter(tag.count)}</p> : null}</span>
            : tag.name}
        </Button>
      ) : <p style={{ fontSize: "14px" }}>We have some issues with loading tags. Please close and open the dialog and try again.</p>}
      {tags.length > 3 && !expanded ?
        <Button
          sx={{
            minWidth: "2.5rem !important",
            minHeight: "2.5rem !important",
            padding: "0 !important",
            background: colors.redPink,
            color: colors.white,
          }}
          onClick={onMoreClick}>
          <img src={plusIcon} alt="More tags" style={{ maxWidth: "16px" }} />
        </Button> : null}
    </Container>
  );
};

const Container = styled("div", { shouldForwardProp: (prop) => prop !== "isExpanded" }
)<{ isExpanded: boolean }>(({ isExpanded }) => {
  return {
    marginTop: "1rem",
    display: "flex",
    flexWrap: "wrap",
    "> button": {
      marginRight: "0.5rem",
      padding: "7px 12px",
      borderRadius: "24px",
      minWidth: "3rem",
      maxWidth: "10rem",
      width: "auto",
      marginBottom: "0.5rem",
      fontSize: "12px",
      border: `1px solid ${colors.redPink}`,
      "&:hover": {
        background: colors.selectedCollectionItem,
        opacity: 0.8
      }
    }
  };
});

export default SelectTags;
